import {
    BACKEND_URL,
    clearStorage,
    ExtendedStreamingInfo,
    fetchInit,
    getMMToken,
    needsLogin,
    PrimaryInfo, REAUTH_REQUIRED, spotifyAuth, SpotifyReauthBanner, useSpotifySession
} from "../util/util";
import React, {useEffect, useState} from "react";
import {FileRejection, useDropzone} from "react-dropzone";
import {Link} from "react-router-dom";
import JSZip from "jszip";

const maxFileSize = 30000000
const maxJsonFiles = 15;
const maxZipFiles = 1;

export const Account = () => {

    const [loggedIn, setLoggedIn] = useState<boolean>(false);
    const spotifySession = useSpotifySession();

    useEffect(() => {
        sessionStorage.setItem('route', 'account');
        if (needsLogin()) {
            spotifyAuth();
        } else setLoggedIn(true);
    }, []);

    return (
        <div>
            {loggedIn && (
                <>
                    <PrimaryInfo text={'Account Information'}/>
                    <SpotifyReauthBanner session={spotifySession} uploadIsOnThisPage={true}/>
                    <div className={'text-mm-text my-8 mx-auto w-1/2 portrait:w-[90%] text-2xl portrait:text-base flex flex-col gap-2'}>
                        <div className={'flex flex-row justify-between gap-8'}>
                            <div>Username</div>
                            <div>{truncateStr(localStorage.getItem('username')!)}</div>
                        </div>
                        <div className={'flex flex-row justify-between gap-8'}>
                            <div>Display Name</div>
                            <div>{truncateStr(localStorage.getItem('display_name')!)}</div>
                        </div>
                        <div className={'flex flex-row justify-between gap-8'}>
                            <div>Email</div>
                            <div>{localStorage.getItem('email')}</div>
                        </div>
                        <div className={'flex flex-row justify-between gap-8'}>
                            <div>Account Created</div>
                            <div>{unixMillisToString(localStorage.getItem('timestamp')!)}</div>
                        </div>
                        <div className={'flex flex-row justify-between gap-8'}>
                            <Link to={'/'} className={'text-mm-cyan no-underline'} onClick={() => clearStorage()}>Logout</Link>
                            <div></div>
                        </div>
                    </div>
                    <Dropzone/>
                </>
            )}
        </div>
    );
}

const Dropzone = () => {
    const [zipFiles, setZipFiles] = useState<File[]>([]);
    const [jsonFiles, setJsonFiles] = useState<File[]>([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [popupVisible, setPopupVisible] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    const onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        setLoadingMessage('Loading...');
        setErrorMessage('');
        setHoveredIndex(-1);

        let numZips = 0;
        let numJson = 0;
        acceptedFiles.forEach((file: File) => {
            if (file.type.includes('zip')) numZips++;
            if (file.type.includes('json')) numJson++;
        });

        if (acceptedFiles?.length) {
            if (zipFiles.length + numZips > maxZipFiles || jsonFiles.length + numJson > maxJsonFiles) {
                setLoadingMessage('');
                setErrorMessage('Too many files');
                return;
            }
            setErrorMessage('');
        }
        if (rejectedFiles?.length) {
            setLoadingMessage('');
            setErrorMessage('Only .zip files under 30MB are accepted');
            return;
        }

        const newZipFiles: File[] = [];
        const newJsonFiles: File[] = [];
        const promises: Promise<any>[] = [];
        acceptedFiles.forEach(file => {
            if (file.type.includes('json')) {
                const reader = new FileReader();
                const promise = new Promise((resolve, reject) => {
                    reader.onload = (event) => {
                        if (isFormattedCorrectly(String(event.target!.result))) {
                            newJsonFiles.push(file);
                            resolve(file);
                        } else reject();
                    }
                    reader.readAsText(file);
                }).then();
                promises.push(promise);
            } else newZipFiles.push(file);
        })

        Promise.all(promises).then(() => {
            setLoadingMessage('');
            const uniqueZipFiles = newZipFiles.filter(file => !zipFiles.some(f => f.name === file.name));
            const uniqueJsonFiles = newJsonFiles.filter(file => !jsonFiles.some(f => f.name === file.name));
            setZipFiles(previousFiles => [...previousFiles, ...uniqueZipFiles]);
            setJsonFiles(previousFiles => [...previousFiles, ...uniqueJsonFiles]);
        }).catch(() => {
            setLoadingMessage('');
            setErrorMessage('One or more of the files you uploaded are not formatted correctly. Make sure they are the correct files and try again.');
        });

    }

    const removeItem = (item: File) => {
        if (errorMessage === 'Too many files') setErrorMessage('');
        setZipFiles(zipFiles.filter(file => file.name !== item.name));
        setJsonFiles(jsonFiles.filter(file => file.name !== item.name));
    }

    const postJsonToServer = (data: string, resolve: (value: unknown) => void, reject: (reason?: any) => void) => {
        fetch(BACKEND_URL + '/api/data/' + localStorage.getItem('username'), fetchInit('data', data, getMMToken()!))
            .then(async response => {
                // This endpoint needs a live Spotify token to look up track metadata, so it is the one
                // place an expired authorization blocks the request outright.
                if (response.status === 401 && (await response.text()).includes(REAUTH_REQUIRED)) {
                    return Promise.reject(REAUTH_REQUIRED);
                }
                return response.json();
            })
            .then(data => resolve(data))
            .catch(error => reject(error));
    }

    const submit = () => {
        setLoadingMessage('Loading...');
        setErrorMessage('');

        const uploadPromises: Promise<any>[] = [];
        zipFiles.forEach(file => {
            const reader = new FileReader();
            const promise = new Promise((resolve, reject) => {
                reader.onload = async (event) => {
                    if (!event.target?.result) return;
                    const zip = await JSZip.loadAsync(event.target.result);
                    let numValidFiles = 0;
                    for (const fileName of Object.keys(zip.files)) {
                        if (fileName.endsWith('.json')) {
                            const fileContent = await zip.files[fileName].async('string');
                            if (isFormattedCorrectly(fileContent)) {
                                numValidFiles++;
                                postJsonToServer(fileContent, resolve, reject);
                            }
                        }
                    }
                    if (numValidFiles === 0) reject('No valid files in this .zip');
                }
                reader.readAsArrayBuffer(file);
            });
            uploadPromises.push(promise);
        });
        jsonFiles.forEach(file => {
            const reader = new FileReader();
            const promise = new Promise((resolve, reject) => {
                reader.onload = (event) => {
                    postJsonToServer(String(event.target!.result), resolve, reject);
                }
                reader.readAsText(file);
            });
            uploadPromises.push(promise);
        });

        Promise.all(uploadPromises).then(() => {
            setZipFiles([]);
            setJsonFiles([]);
            setLoadingMessage('Success! You will be able to view your updated stats within an hour.');
        }).catch(error => {
            console.error(error);
            setLoadingMessage('');
            if (error === REAUTH_REQUIRED) {
                setErrorMessage('Your Spotify connection expired. Reconnect Spotify above, then upload again.');
                return;
            }
            setErrorMessage('There seems to be a problem with the file(s) you uploaded. Make sure you have the correct file(s) and try again.');
        });
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/json': ['.json'],
            'application/zip': ['.zip'],
            'application/x-zip-compressed': ['.zip'],
        },
        maxSize: maxFileSize,
    });

    return (
        <div className={'w-1/2 portrait:w-[90%] text-mm-text mx-auto mb-20'}>
            <div className={'text-[0.8rem]'}>
                Upload your extended streaming history here.
                <span className={'text-mm-cyan cursor-pointer'} onClick={() => setPopupVisible(true)}> <u>What's this?</u></span>
            </div>
            <div {...getRootProps({
                className: 'flex flex-col justify-center items-center p-4 border-2 border-dashed border-mm-green rounded-[5px] mx-auto my-[0.2rem] text-mm-text'
            })}>
                <input {...getInputProps()} />
                {isDragActive ? (
                    <p>Drop the file here ...</p>
                ) : (
                    <p>Drag and drop .zip file here, or click to select file</p>
                )}
            </div>
            {loadingMessage !== '' && <div className={'text-mm-cyan w-full text-center'}>{loadingMessage}</div>}
            {errorMessage !== '' && <p className={'text-mm-red my-2'}>{errorMessage}</p>}
            <ul className={'list-none flex flex-wrap justify-start p-0 gap-2 my-2 mx-auto'}>
                {(zipFiles.concat(jsonFiles)).map((file, index) => (
                    <li key={file.name} style={{position: 'relative'}}>
                        <div
                            className={'border border-mm-text rounded p-2'}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(-1)}
                        >
                            {file.name}
                        </div>
                        {hoveredIndex === index && <div className={'bg-mm-red text-mm-dark cursor-pointer absolute top-[0.1rem] right-[0.1rem] border border-mm-dark rounded-full w-[0.6rem] h-[0.6rem] text-[0.5rem] flex justify-center items-center'} onClick={() => removeItem(file)} onMouseEnter={() => setHoveredIndex(index)}></div>}
                    </li>
                ))}
            </ul>
            {(zipFiles.length + jsonFiles.length) !== 0 && (
                <div className={'flex justify-center w-full'}>
                    <div className={'bg-mm-green p-[0.6rem] rounded-[0.6rem] inline-block text-mm-dark m-[0.6rem] text-center hover:bg-mm-text hover:cursor-pointer mx-auto flex justify-center'} onClick={submit}><b>SUBMIT</b></div>
                </div>
            )}
            {popupVisible && (
                <ExtendedStreamingInfo callback={() => setPopupVisible(false)} />
            )}
        </div>
    );
}

const unixMillisToString = (unixMillis: string) => {
    const date = new Date(+unixMillis);
    return date.toLocaleDateString();
}

const truncateStr = (str: string) => {
    if (!str) return null;
    let num = 25;
    if (str.length <= num) return str;
    return str.slice(0, num) + '...';
}

const isFormattedCorrectly = (file: string) => {
    let newFile;
    try {
        newFile = JSON.parse(file);
    } catch (e) {
        return false;
    }

    if (!Array.isArray(newFile)) {
        return false;
    }

    for (let i = 0; i < newFile.length; i++) {
        const item = newFile[i];

        if (
            typeof item !== 'object' ||
            !item.hasOwnProperty('ts') ||
            !item.hasOwnProperty('platform') ||
            !item.hasOwnProperty('ms_played') ||
            (!item.hasOwnProperty('ip_addr') && !item.hasOwnProperty('ip_addr_decrypted')) ||
            !item.hasOwnProperty('conn_country') ||
            !item.hasOwnProperty('master_metadata_track_name') ||
            !item.hasOwnProperty('master_metadata_album_artist_name') ||
            !item.hasOwnProperty('master_metadata_album_album_name') ||
            !item.hasOwnProperty('spotify_track_uri') ||
            !item.hasOwnProperty('episode_name') ||
            !item.hasOwnProperty('episode_show_name') ||
            !item.hasOwnProperty('spotify_episode_uri') ||
            !item.hasOwnProperty('reason_start') ||
            !item.hasOwnProperty('reason_end') ||
            !item.hasOwnProperty('shuffle') ||
            !item.hasOwnProperty('skipped') ||
            !item.hasOwnProperty('offline') ||
            !item.hasOwnProperty('offline_timestamp') ||
            !item.hasOwnProperty('incognito_mode')
        ) {
            return false;
        }
    }

    return true;
}
